/**
 * Editing the day's 6 Deye windows (TZ-UI «6 вікон Deye»): drag a boundary on
 * the timeline (step 1 hour) or change a window in the table. The rules are
 * those of `SiteWindowsResponse` with E = `elapsed_hours`:
 *   - a window ending at or before E has elapsed and does not change;
 *   - a window starting at or before E keeps its start (the one in progress
 *     may change mode, power and SOC);
 *   - moved starts are after E, between the neighbours' starts.
 * Every operation returns exactly 6 windows: nothing is added or removed.
 */
import { DEYE_WINDOWS_PER_DAY, type DeyeWindow, type DeyeWindowMode } from 'shared';

export interface WindowLimits {
  /** Day length, hours: 23 / 24 / 25. */
  hours: number;
  /** Fully elapsed hours of the day. */
  elapsed: number;
  pMaxKw: number;
  socMinPct: number;
  socMaxPct: number;
  /** Positions that can't start a window (the repeated 03:00 of the autumn clock change). */
  blocked?: readonly number[];
  /**
   * Owner, 24.10.2026 — the plan's limits kept by a hand edit (`SiteWindowsResponse.limits`):
   * a grid charge at most this per hour position (the allowed power less the
   * expected load); absent — the inverter's power only.
   */
  chargeCapKw?: readonly number[] | null;
  /** A discharge stops no lower: max(SOC min, outage reserve); absent — SOC min. */
  socFloorPct?: number;
  /** The battery currents on the inverter are Phage's, not the installer's: no grid charge. */
  currentsByPhage?: boolean;
}

export function windowEnd(ws: readonly DeyeWindow[], i: number, hours: number): number {
  return ws[i + 1]?.start_hour ?? hours;
}

export function isElapsed(ws: readonly DeyeWindow[], i: number, lim: WindowLimits): boolean {
  return windowEnd(ws, i, lim.hours) <= lim.elapsed;
}

export function isRunning(ws: readonly DeyeWindow[], i: number, lim: WindowLimits): boolean {
  return (ws[i]?.start_hour ?? 0) <= lim.elapsed && !isElapsed(ws, i, lim);
}

/** The first window starts at 00:00; a started window keeps its start. */
export function startLocked(ws: readonly DeyeWindow[], i: number, lim: WindowLimits): boolean {
  return i === 0 || (ws[i]?.start_hour ?? 0) <= lim.elapsed;
}

/** Where window `i` may start: after E and the previous start, before its own end. */
export function startOptions(ws: readonly DeyeWindow[], i: number, lim: WindowLimits): number[] {
  if (startLocked(ws, i, lim)) return [];
  const prev = ws[i - 1]?.start_hour ?? 0;
  const lo = Math.max(prev + 1, lim.elapsed + 1);
  const hi = windowEnd(ws, i, lim.hours) - 1;
  const out: number[] = [];
  for (let h = lo; h <= hi; h += 1) if (!lim.blocked?.includes(h)) out.push(h);
  return out;
}

/** Hour under the pointer: a fraction of the day's width, rounded to a whole hour. */
export function hourAt(fraction: number, hours: number): number {
  return Math.round(Math.min(1, Math.max(0, fraction)) * hours);
}

const replace = (ws: readonly DeyeWindow[], i: number, w: DeyeWindow): DeyeWindow[] =>
  ws.map((x, k) => (k === i ? w : x));

/** Move the start of window `i` (the boundary with window `i − 1`) to the allowed hour nearest `hour`. */
export function moveStart(
  ws: readonly DeyeWindow[],
  i: number,
  hour: number,
  lim: WindowLimits,
): readonly DeyeWindow[] {
  const opts = startOptions(ws, i, lim);
  const w = ws[i];
  if (!w || opts.length === 0 || !Number.isFinite(hour)) return ws;
  const h = Math.round(hour);
  let target = opts[0] ?? w.start_hour;
  for (const o of opts) if (Math.abs(o - h) < Math.abs(target - h)) target = o;
  return target === w.start_hour ? ws : replace(ws, i, { ...w, start_hour: target });
}

/** The lowest SOC of a window of `mode`: a discharge stops at the floor. */
const socLow = (mode: DeyeWindowMode, lim: WindowLimits) =>
  mode === 'self_consumption' ? (lim.socFloorPct ?? lim.socMinPct) : lim.socMinPct;
const clampSoc = (v: number, mode: DeyeWindowMode, lim: WindowLimits) =>
  Math.min(lim.socMaxPct, Math.max(socLow(mode, lim), Math.round(v)));

/**
 * The most power window `i` of `mode` may take, kW: a discharge the
 * inverter's; a grid charge the least cap of its hours still to run (as the
 * plan caps it), else the inverter's.
 */
export function windowMaxKw(
  ws: readonly DeyeWindow[],
  i: number,
  mode: DeyeWindowMode,
  lim: WindowLimits,
): number {
  if (mode === 'grid_charge' && lim.currentsByPhage) return 0;
  if (mode !== 'grid_charge' || !lim.chargeCapKw) return lim.pMaxKw;
  // A grid charge has its own limit (Max A Charge), not the discharge's (25.10.2026).
  let max = Number.POSITIVE_INFINITY;
  const start = Math.max(ws[i]?.start_hour ?? 0, lim.elapsed);
  for (let h = start; h < windowEnd(ws, i, lim.hours); h += 1)
    max = Math.min(max, lim.chargeCapKw[h] ?? lim.pMaxKw);
  return Number.isFinite(max) ? max : lim.pMaxKw;
}

const clampPower = (v: number, max: number) => Math.min(max, Math.max(0.1, Math.round(v * 10) / 10));

/**
 * Change the mode of window `i`: «Заряд з мережі» charges up to SOC max by
 * default, «Власне споживання» discharges down to SOC min, «Утримання заряду»
 * has no power and no SOC.
 */
export function setMode(
  ws: readonly DeyeWindow[],
  i: number,
  mode: DeyeWindowMode,
  lim: WindowLimits,
): readonly DeyeWindow[] {
  const w = ws[i];
  if (!w || w.mode === mode || isElapsed(ws, i, lim)) return ws;
  const start_hour = w.start_hour;
  const power = w.mode === 'hold' ? 0 : w.power_kw;
  if (mode === 'hold') return replace(ws, i, { start_hour, mode, power_kw: 0, soc_pct: null });
  // The new mode's limits: a grid charge within the connection's allowed
  // power, a discharge no lower than the floor (owner, 24.10.2026).
  const max = windowMaxKw(ws, i, mode, lim);
  if (mode === 'grid_charge') {
    return replace(ws, i, {
      start_hour,
      mode,
      power_kw: clampPower(power > 0 ? power : lim.pMaxKw / 2, max),
      soc_pct: lim.socMaxPct,
    });
  }
  return replace(ws, i, {
    start_hour,
    mode,
    power_kw: clampPower(power > 0 ? power : lim.pMaxKw, max),
    soc_pct: socLow(mode, lim),
  });
}

export function setPower(
  ws: readonly DeyeWindow[],
  i: number,
  kw: number,
  lim: WindowLimits,
): readonly DeyeWindow[] {
  const w = ws[i];
  if (!w || w.mode === 'hold' || !Number.isFinite(kw) || isElapsed(ws, i, lim)) return ws;
  const power_kw = clampPower(kw, windowMaxKw(ws, i, w.mode, lim));
  return power_kw === w.power_kw ? ws : replace(ws, i, { ...w, power_kw });
}

export function setSoc(
  ws: readonly DeyeWindow[],
  i: number,
  pct: number,
  lim: WindowLimits,
): readonly DeyeWindow[] {
  const w = ws[i];
  if (!w || w.mode === 'hold' || !Number.isFinite(pct) || isElapsed(ws, i, lim)) return ws;
  const soc_pct = clampSoc(pct, w.mode, lim);
  return soc_pct === w.soc_pct ? ws : replace(ws, i, { ...w, soc_pct });
}

/**
 * What the draft becomes when the server's windows change: the server's —
 * unless the user edited the previous ones on the same day. A new day or no
 * windows at all always drops the draft.
 */
export function followServer(
  draft: readonly DeyeWindow[] | null,
  prev: { windows: readonly DeyeWindow[] | null; date: string },
  next: { windows: readonly DeyeWindow[] | null; date: string },
): readonly DeyeWindow[] | null {
  const edited = draft !== null && !sameWindows(draft, prev.windows);
  return next.windows !== null && prev.date === next.date && edited ? draft : next.windows;
}

export function sameWindows(a: readonly DeyeWindow[] | null, b: readonly DeyeWindow[] | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Why `after` is not an allowed edit of `before` (null — it is): the checks
 * the server makes on `PUT /sites/:id/windows`, so the editor never offers a
 * program the inverter would refuse.
 */
export function editProblem(
  before: readonly DeyeWindow[],
  after: readonly DeyeWindow[],
  lim: WindowLimits,
): string | null {
  if (after.length !== DEYE_WINDOWS_PER_DAY) return 'count';
  if (after[0]?.start_hour !== 0) return 'first';
  for (let i = 1; i < after.length; i += 1) {
    if ((after[i]?.start_hour ?? 0) <= (after[i - 1]?.start_hour ?? 0)) return 'order';
  }
  if ((after.at(-1)?.start_hour ?? 0) >= lim.hours) return 'order';
  for (let i = 0; i < before.length; i += 1) {
    const b = before[i];
    if (!b) continue;
    if (isElapsed(before, i, lim) && !sameWindows([b], [after[i] ?? b])) return 'elapsed';
    if (isElapsed(before, i, lim) && windowEnd(after, i, lim.hours) !== windowEnd(before, i, lim.hours)) {
      return 'elapsed';
    }
    if (b.start_hour <= lim.elapsed && after[i]?.start_hour !== b.start_hour) return 'started';
  }
  for (let i = 0; i < after.length; i += 1) {
    const a = after[i];
    if (!a) continue;
    if (a.start_hour > lim.elapsed && lim.blocked?.includes(a.start_hour)) return 'blocked';
    if (a.start_hour !== before[i]?.start_hour && a.start_hour <= lim.elapsed) return 'started';
  }
  return valueProblem(after, lim)?.kind ?? null;
}

/**
 * The first window still to run whose power is above the inverter's, whose
 * grid charge is above the connection's allowed power less the load, or whose
 * SOC is outside the object's limits (they may have changed since the plan was
 * built, or a window moved); elapsed windows are not checked, as on the server.
 */
export function valueProblem(
  ws: readonly DeyeWindow[],
  lim: WindowLimits,
): { kind: 'power' | 'grid' | 'no_grid' | 'soc'; window: number; max?: number; min?: number } | null {
  for (let i = 0; i < ws.length; i += 1) {
    const w = ws[i];
    if (!w || w.mode === 'hold' || isElapsed(ws, i, lim)) continue;
    const max = windowMaxKw(ws, i, w.mode, lim);
    // No grid charge in these hours (owner, 24.10.2026): the installer's limits or Phage's currents.
    if (w.mode === 'grid_charge' && max <= 0) return { kind: 'no_grid', window: i };
    if (w.power_kw <= 0 || (w.mode !== 'grid_charge' && w.power_kw > lim.pMaxKw + 1e-9)) {
      return { kind: 'power', window: i };
    }
    if (w.power_kw > max + 1e-9) return { kind: 'grid', window: i, max };
    const min = socLow(w.mode, lim);
    if (w.soc_pct < min || w.soc_pct > lim.socMaxPct) return { kind: 'soc', window: i, min };
  }
  return null;
}
